import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Grid, InputBase, InputAdornment, IconButton, Tooltip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { SearchOutlined } from '@material-ui/icons';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import { setSearchQuery } from '../../redux/modules/search';
import { fetchPlaces } from '../../redux/modules/place';
import { sendEventToGoogleAnalytics } from '../../common/helpers';
import useStyles from './SearchBox.styles';

const SearchBox = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const searchQuery = useSelector((state) => state.search.searchQuery);
  const searchSuggestions = useSelector((state) => state.search.searchSuggestions);
  const fetchingSearchSuggestions = useSelector((state) => state.search.fetchingSearchSuggestions);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(fetchingSearchSuggestions);
  const [suggestions, setSuggestions] = useState(searchSuggestions);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipText = `<div>
    <span style="color: #222222; font-weight: 700">Search for things like: </span>
    <ul style="color: #696969;">
      <li>Grocery</li>
      <li>Grocery near San Francisco, CA</li>
      <li>Trader Joe’s</li>
      <li>Search parks, airports, restaurants <br />
      — any kind of business!</li>
    </ul>
  </div>`;

  const handleSearchInputOnChange = (event) => {
    setShowTooltip(false);
    dispatch(setSearchQuery(event.target.value));
  };

  const handleSuggestionSelect = (value) => {
    history.push('/places/' + value);
    dispatch(setSearchQuery(value));
    dispatch(fetchPlaces());
  };

  const handleOnClick = () => {
    if (searchQuery.trim().length) {
      sendEventToGoogleAnalytics('search_places');
      history.push('/places/' + searchQuery);
      dispatch(fetchPlaces());
    }
  };

  const handleSearchEnter = (event) => {
    if (event.key === 'Enter' && searchQuery.trim().length) {
      sendEventToGoogleAnalytics('search_places');
      history.push('/places/' + searchQuery);
      dispatch(fetchPlaces());
    }
  };

  const toggleTooltipHandler = (value) => {
    setShowTooltip(value);
  };

  const getInputField = (params) => {
    return (
      <InputBase
        className={classes.searchInput}
        placeholder="Search places by name, type and/or address"
        fullWidth
        onChange={handleSearchInputOnChange}
        onKeyDown={handleSearchEnter}
        ref={params.InputProps.ref}
        inputProps={params.inputProps}
        endAdornment={
          <InputAdornment position="end">
            <IconButton className={classes.searchButton} onClick={handleOnClick}>
              <SearchOutlined fontSize="small" style={{ fill: '#ffffff' }} />
            </IconButton>
          </InputAdornment>
        }
      />
    );
  };

  useEffect(() => {
    setSuggestions(searchSuggestions);
    setIsLoadingSuggestions(fetchingSearchSuggestions);
  }, [searchSuggestions, fetchingSearchSuggestions]);

  return (
    <div className={classes.root}>
      {props.showHints ? (
        <Tooltip
          title={parse(tooltipText)}
          open={showTooltip}
          arrow
          placement="top-start"
          classes={{ tooltip: [classes.tooltip, classes.tooltipTop].join(' '), arrow: classes.tooltipArrow }}
        >
          <div className={classes.tooltipRef} />
        </Tooltip>
      ) : null}
      <Grid container direction="row" className={classes.searchBox}>
        <Grid item xs={12}>
          <Autocomplete
            classes={{ input: classes.input }}
            freeSolo
            fullWidth
            value={searchQuery}
            disableClearable
            loading={isLoadingSuggestions}
            options={suggestions.map((s) => s.suggestion)}
            onChange={(e, v) => handleSuggestionSelect(v)}
            onFocus={() => toggleTooltipHandler(true)}
            onBlur={() => toggleTooltipHandler(false)}
            renderInput={(params) => getInputField(params)}
          />
        </Grid>
      </Grid>
    </div>
  );
};

SearchBox.propTypes = {
  showHints: PropTypes.bool
};

export default SearchBox;
